/**
 * useDiscountNudge — the ONE stage decision for the hybrid unused-incentive
 * nudge (operator decision 2026-08-12):
 *
 *   stage "banner"  — full-width UnusedDiscountBanner (ProLayout) until the
 *                     pro dismisses it once.
 *   stage "chip"    — after that first dismissal, FOREVER: the compact
 *                     header DiscountChip (HeaderNav). No more banners, no
 *                     per-session returns — the chip is the ambient
 *                     persistent reminder.
 *   null            — nothing: no unconsumed incentive (incl. subscribed —
 *                     both surfaces disappear permanently on activation),
 *                     billing unknown (fail quiet), Sill Pro rollout flag
 *                     off, or the Settings ActivationBanner is on screen
 *                     (never a contradictory "credit waiting" beside
 *                     "Activating Sill Pro…").
 *
 * The dismissal is DURABLE and account-keyed (localStorage, Clerk user id) —
 * deliberately unlike NoStandingOffersBanner's per-session dismiss: the
 * banner→chip handoff replaces the "returns every session" behavior. Banner
 * and chip are mounted simultaneously (ProLayout + HeaderNav), so the
 * dismissal must propagate in-render — a zustand bump signal invalidates
 * every subscriber when `dismissDiscountBanner()` writes the flag.
 *
 * Under IS_PREVIEW both surfaces are disarmed unless the explicit
 * `?discount=banner|chip` param forces a stage (the default preview billing
 * fixture carries a full starter promise — see UnusedDiscountBanner).
 */
import { create } from "zustand";
import { usePostHog } from "@posthog/react";
import { useEffect, useRef } from "react";
import {
  activationIncentive,
  type ActivationIncentive,
} from "../lib/activation-incentive";
import { readClerkUserId } from "../lib/pro-identity";
import { IS_PREVIEW, previewQueryParam } from "../lib/pro-mode";
import { useActivationBannerStore } from "../stores/activationBanner";
import { useCRMBilling, useProfile } from "./useProData";
import { useSillProEnabled } from "./useSillProEnabled";

export type DiscountNudgeStage = "banner" | "chip";

export interface DiscountNudge {
  stage: DiscountNudgeStage;
  incentive: ActivationIncentive;
}

/** localStorage key for the durable banner-dismissed flag, keyed per account. */
export function discountBannerDismissedKey(userId: string): string {
  return `sill.discountBanner.dismissed:${userId}`;
}

function readBannerDismissed(): boolean {
  try {
    return (
      localStorage.getItem(discountBannerDismissedKey(readClerkUserId())) !==
      null
    );
  } catch {
    // Storage unavailable — treat as dismissed: the quiet chip is the
    // harmless failure mode, a banner that can never be dismissed is not.
    return true;
  }
}

// Re-render signal for the localStorage write: the flag itself lives in
// storage (read fresh each render so late Clerk hydration resolves the right
// account key), the store only bumps subscribers when it changes.
const useDismissBump = create<{ n: number; bump: () => void }>((set) => ({
  n: 0,
  bump: () => set((s) => ({ n: s.n + 1 })),
}));

/** Write the durable dismissal flag and notify every mounted nudge surface. */
export function dismissDiscountBanner(): void {
  try {
    localStorage.setItem(discountBannerDismissedKey(readClerkUserId()), "1");
  } catch {
    // Best-effort — worst case the banner shows again next load.
  }
  useDismissBump.getState().bump();
}

export function useDiscountNudge(): DiscountNudge | null {
  // Subscribe to the bump so a banner dismissal re-renders the chip (and the
  // banner itself) immediately.
  useDismissBump((s) => s.n);
  const sillProEnabled = useSillProEnabled();
  const billing = useCRMBilling();
  const profile = useProfile();
  const activationBannerVisible = useActivationBannerStore((s) => s.visible);

  const incentive = activationIncentive(
    billing.data,
    profile.data?.founding?.member ?? false,
  );
  if (!incentive) return null;

  if (IS_PREVIEW) {
    const forced = previewQueryParam("discount");
    if (forced === "banner" || forced === "chip") {
      return { stage: forced, incentive };
    }
    return null;
  }

  if (!sillProEnabled || activationBannerVisible) return null;
  return { stage: readBannerDismissed() ? "chip" : "banner", incentive };
}

/**
 * Fire a nudge-surface "shown" analytics event once per mount-visibility.
 * Shared by the banner and the chip so both follow the same once-per-mount
 * discipline (mirrors NoStandingOffersBanner's shownCapturedRef idiom).
 */
export function useNudgeShownEvent(
  event: string,
  variant: string | null,
): void {
  const posthog = usePostHog();
  const capturedRef = useRef(false);
  useEffect(() => {
    if (variant !== null && !capturedRef.current) {
      capturedRef.current = true;
      posthog?.capture(event, { variant });
    }
  }, [event, variant, posthog]);
}
